import { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { ChatScreen, type ChatCopy } from './ChatScreen';
import { colors } from '../styles/theme';
import { Modal } from '../components/Modal';
import { Input } from '../components/Input';
import { errorMessage } from '../utils/errorMessage';
import type {
  CoachExchange,
  CoachPreferences,
  CoachSource,
  CoachWorkspace,
  CoachMemory,
} from '../types/coach';

export interface CoachClient {
  load(): Promise<CoachWorkspace>;
  ask(question: string, requestId: string): Promise<CoachExchange>;
  /** Check-ins and saved notes belong to the main chat; chats about one topic leave these out. */
  savePreferences?(preferences: CoachPreferences): Promise<CoachPreferences>;
  saveMemory?(text: string, requestId: string): Promise<CoachMemory>;
  changeMemory?(memory: CoachMemory, text: string | null): Promise<unknown>;
  prepareCheckins?(): Promise<void>;
  /** Staff can open the conversation behind a source; everyone sees the quoted excerpt. */
  openSource?(source: CoachSource): void;
}

export function CoachScreen({
  client,
  onConnect,
  hasMessages = true,
  copy,
}: {
  client: CoachClient;
  onConnect?: () => void;
  hasMessages?: boolean;
  copy?: ChatCopy;
}) {
  const [data, setData] = useState<CoachWorkspace | null>(null);
  const [error, setError] = useState('');
  const [checkinError, setCheckinError] = useState('');
  const [saving, setSaving] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [memoryText, setMemoryText] = useState('');
  const [editingMemory, setEditingMemory] = useState<CoachMemory | null>(null);
  const [memoryError, setMemoryError] = useState('');
  const [memoryBusy, setMemoryBusy] = useState(false);
  const [chatRevision, setChatRevision] = useState(0);
  const [forgetting, setForgetting] = useState<string | null>(null);
  const loadSequence = useRef(0);
  const memoryRequest = useRef<{ id: string; text: string } | null>(null);
  const memoryLock = useRef(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    let canceled = false;
    const sequence = ++loadSequence.current;
    setError('');
    client
      .load()
      .then((value) => {
        if (!canceled && sequence === loadSequence.current) setData(value);
      })
      .catch(() => {
        if (!canceled) setError('Couldn’t open your chat. Please try again.');
      });
    return () => {
      canceled = true;
      active.current = false;
    };
  }, [client, reload]);

  const enabled = Boolean(data && data.preferences.frequency !== 'off');
  const updateMemory = async (forget?: CoachMemory) => {
    if (memoryLock.current || !client.saveMemory || !client.changeMemory) return;
    memoryLock.current = true;
    setMemoryBusy(true);
    setMemoryError('');
    ++loadSequence.current;
    try {
      if (forget) await client.changeMemory(forget, null);
      else if (editingMemory) await client.changeMemory(editingMemory, memoryText.trim());
      else {
        if (memoryRequest.current?.text !== memoryText.trim())
          memoryRequest.current = { id: crypto.randomUUID(), text: memoryText.trim() };
        await client.saveMemory(memoryRequest.current.text, memoryRequest.current.id);
      }
      if (!active.current) return;
      const sequence = ++loadSequence.current;
      const workspace = await client.load();
      if (!active.current || sequence !== loadSequence.current) return;
      setData(workspace);
      if (forget || editingMemory) setChatRevision((value) => value + 1);
      if (!forget || forget.id === editingMemory?.id) {
        setEditingMemory(null);
        setMemoryText('');
        memoryRequest.current = null;
      }
      setForgetting(null);
    } catch (failure) {
      if (active.current)
        setMemoryError(errorMessage(failure, 'Couldn’t update your memory. Please try again.'));
    } finally {
      memoryLock.current = false;
      if (active.current) setMemoryBusy(false);
    }
  };
  const setCheckins = async (allow: boolean) => {
    if (!data || saving || !client.savePreferences) return;
    setSaving(true);
    setCheckinError('');
    try {
      if (allow) await client.prepareCheckins?.();
      if (!active.current) return;
      const preferences = await client.savePreferences({
        ...data.preferences,
        frequency: allow ? 'weekly' : 'off',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (active.current) {
        setData((current) => (current ? { ...current, preferences } : current));
        setConsentOpen(false);
      }
    } catch (failure) {
      if (active.current)
        setCheckinError(errorMessage(failure, 'Couldn’t update check-ins. Please try again.'));
    } finally {
      if (active.current) setSaving(false);
    }
  };

  return (
    <View style={styles.container}>
      {data && (client.saveMemory || client.savePreferences) && (
        <View style={styles.toolbar}>
          {client.saveMemory && client.changeMemory && (
            <TouchableOpacity
              accessibilityRole="button"
              onPress={() => {
                setMemoryError('');
                setMemoryOpen(true);
                const sequence = ++loadSequence.current;
                void client
                  .load()
                  .then((workspace) => {
                    if (active.current && sequence === loadSequence.current) setData(workspace);
                  })
                  .catch(() => {
                    if (active.current && sequence === loadSequence.current)
                      setMemoryError('Couldn’t refresh your memories. Please reopen them.');
                  });
              }}
            >
              <Text style={styles.link}>What I remember</Text>
            </TouchableOpacity>
          )}
          {client.savePreferences && (
            <TouchableOpacity
              onPress={() => {
                setCheckinError('');
                setConsentOpen(true);
              }}
              accessibilityRole="button"
              accessibilityLabel={enabled ? 'Check-ins are on' : 'Keep me posted'}
            >
              <Text style={styles.link}>{enabled ? 'Check-ins are on' : 'Keep me posted'}</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
      {client.saveMemory && client.changeMemory && (
        <Modal visible={memoryOpen} onClose={() => setMemoryOpen(false)} title="What I remember">
          <View style={styles.consent}>
            <Text style={styles.caption}>
              A few things you want me to keep in mind. Add one here, or say “Remember that…” in
              Ask.
            </Text>
            {(data?.memories || []).map((memory) => (
              <View key={memory.id} style={styles.memory}>
                <Text style={styles.memoryText}>{memory.text}</Text>
                <View style={styles.memoryActions}>
                  <TouchableOpacity
                    disabled={memoryBusy}
                    accessibilityRole="button"
                    accessibilityLabel={'Edit memory: ' + memory.text}
                    onPress={() => {
                      setEditingMemory(memory);
                      setMemoryText(memory.text);
                      setMemoryError('');
                    }}
                  >
                    <Text style={styles.link}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    disabled={memoryBusy}
                    accessibilityRole="button"
                    accessibilityLabel={'Forget memory: ' + memory.text}
                    onPress={() => setForgetting(memory.id)}
                  >
                    <Text style={styles.link}>Forget</Text>
                  </TouchableOpacity>
                </View>
                {forgetting === memory.id && (
                  <View style={styles.consent}>
                    <Text style={styles.caption}>
                      Forget this note and remove the Ask exchanges made while it was saved?
                    </Text>
                    <View style={styles.memoryActions}>
                      <TouchableOpacity
                        accessibilityRole="button"
                        disabled={memoryBusy}
                        onPress={() => void updateMemory(memory)}
                      >
                        <Text style={styles.link}>Forget note & chat</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        accessibilityRole="button"
                        disabled={memoryBusy}
                        onPress={() => setForgetting(null)}
                      >
                        <Text style={styles.link}>Keep it</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </View>
            ))}
            <Input
              label={editingMemory ? 'Edit your memory' : 'Something to remember'}
              placeholder="I’m making more time for painting."
              value={memoryText}
              onChangeText={setMemoryText}
              maxLength={240}
              multiline
              editable={!memoryBusy}
              inputStyle={styles.memoryInput}
              containerStyle={{ marginBottom: 0 }}
            />
            {Boolean(memoryError) && (
              <View accessibilityRole="alert">
                <Text style={styles.caption}>{memoryError}</Text>
              </View>
            )}
            <TouchableOpacity
              style={[styles.action, (!memoryText.trim() || memoryBusy) && { opacity: 0.5 }]}
              accessibilityRole="button"
              disabled={!memoryText.trim() || memoryBusy}
              onPress={() => void updateMemory()}
            >
              <Text style={styles.actionText}>
                {memoryBusy ? 'One moment…' : editingMemory ? 'Save changes' : 'Remember this'}
              </Text>
            </TouchableOpacity>
            {editingMemory && (
              <TouchableOpacity
                accessibilityRole="button"
                disabled={memoryBusy}
                onPress={() => {
                  setEditingMemory(null);
                  setMemoryText('');
                }}
              >
                <Text style={styles.link}>Cancel edit</Text>
              </TouchableOpacity>
            )}
            <Text style={styles.caption}>
              Editing or forgetting a note removes the Ask exchanges made while it was saved. Your
              synced conversations stay separate.
            </Text>
          </View>
        </Modal>
      )}
      {client.savePreferences && (
        <Modal
          visible={consentOpen}
          onClose={() => setConsentOpen(false)}
          title={enabled ? 'You’re in the loop' : 'Let SoulThread check in?'}
        >
          <View style={styles.consent}>
            <Text style={styles.caption}>
              Get an occasional reflection in your iMessage self-chat when there’s something worth
              noticing in your recent conversations. SoulThread handles the timing.
            </Text>
            <Text style={styles.caption}>
              Your connected Mac sends these to your own number. You can pause them anytime.
            </Text>
            {Boolean(checkinError) && (
              <View accessibilityRole="alert">
                <Text style={styles.caption}>{checkinError}</Text>
              </View>
            )}
            <TouchableOpacity
              style={styles.action}
              disabled={saving}
              onPress={() => void setCheckins(!enabled)}
              accessibilityRole="button"
              accessibilityState={{ disabled: saving }}
            >
              <Text style={styles.actionText}>
                {saving ? 'One moment…' : enabled ? 'Pause check-ins' : 'Allow check-ins'}
              </Text>
            </TouchableOpacity>
          </View>
        </Modal>
      )}
      {Boolean(error) && (
        <View style={styles.error} accessibilityRole="alert">
          <Text style={styles.caption}>{error}</Text>
          <TouchableOpacity onPress={() => setReload((value) => value + 1)}>
            <Text style={styles.link}>Try again</Text>
          </TouchableOpacity>
        </View>
      )}
      {data ? (
        <ChatScreen
          key={chatRevision}
          copy={copy}
          history={data.exchanges}
          onSendMessage={async (question, requestId) => {
            const answer = await client.ask(question, requestId);
            // Refresh saved notes without replacing an in-flight conversation.
            if (memoryLock.current) return answer;
            const sequence = ++loadSequence.current;
            void client
              .load()
              .then((workspace) => {
                if (active.current && sequence === loadSequence.current) setData(workspace);
              })
              .catch(() => {});
            return answer;
          }}
          onOpenSource={client.openSource}
          onConnect={onConnect}
          hasMessages={hasMessages}
        />
      ) : (
        !error && (
          <View style={styles.loading}>
            <ActivityIndicator color={colors.primary} />
            <Text style={styles.caption}>Opening your space…</Text>
          </View>
        )
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 0, backgroundColor: colors.background },
  toolbar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    justifyContent: 'flex-end',
    paddingHorizontal: 24,
    paddingTop: 14,
    paddingBottom: 4,
  },
  caption: { color: colors.textSecondary, fontSize: 14, lineHeight: 23 },
  memory: { padding: 14, borderRadius: 12, backgroundColor: colors.background, gap: 12 },
  memoryText: { color: colors.text, fontSize: 14, lineHeight: 22 },
  memoryActions: { flexDirection: 'row', gap: 24 },
  memoryInput: { backgroundColor: colors.background, minHeight: 80 },
  link: { color: colors.textSecondary, fontSize: 13 },
  consent: { gap: 18 },
  action: { backgroundColor: colors.primary, padding: 14, borderRadius: 12, alignItems: 'center' },
  actionText: { color: colors.text, fontSize: 14, fontWeight: '600' },
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 14 },
  error: { padding: 16, gap: 8 },
});
